import { useMemo, useState } from 'react';
import { BlockStack } from '@shopify/polaris';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetAiConnectionsQuery,
  useDeleteAiConnectionMutation,
} from '@/redux/api/api.caller';
import commonSlice from '@/redux/features/common.slice';
import { tokenSelector } from '@/redux/features/auth.slice';
import {
  dismissAskCard,
  makeAskCardDismissedSelector,
} from '@/redux/features/aiPromo.slice';
import { AI_STRINGS, buildClaudeConnectorUrl } from './config';
import AskAiAssistantCard from './components/AskAiAssistantCard';
import ProviderHubCard from './components/ProviderHubCard';
import HowToConnectModal, { ProviderKey } from './components/HowToConnectModal';
import DisconnectModal from './components/DisconnectModal';

const AiAssistants = () => {
  // Raw useSelector/useDispatch intentional — typed wrappers in src/redux/store pull axios ESM that breaks Jest.
  const dispatch = useDispatch();
  const { token } = useSelector(tokenSelector);
  const shop = (token?.shop as string) ?? '';
  const askCardDismissedSelector = useMemo(
    () => makeAskCardDismissedSelector(shop),
    [shop],
  );
  const isAskCardDismissed = useSelector(askCardDismissedSelector);

  const { data, isFetching, error, refetch } = useGetAiConnectionsQuery();
  const [deleteConnection, deleteState] = useDeleteAiConnectionMutation();
  // BE guarantees ≤1 Claude connection per shop, but filter by clientName defensively
  // in case the endpoint grows to return other AI providers in the future.
  const claudeConnection = data?.data?.find((c) => c.clientName === 'Claude') ?? data?.data?.[0] ?? null;

  const [howToProvider, setHowToProvider] = useState<ProviderKey | null>(null);
  const [showDisconnect, setShowDisconnect] = useState(false);

  const handleConnectClaude = () => {
    window.open(buildClaudeConnectorUrl(), '_blank');
  };

  const handleConfirmDisconnect = async () => {
    if (!claudeConnection) return;
    const result = await deleteConnection({ id: claudeConnection.id });
    if ('data' in result) {
      setShowDisconnect(false);
      dispatch(
        commonSlice.actions.handleToast({
          isOpen: true,
          content: 'Disconnected successfully',
          error: false,
        }),
      );
    } else {
      dispatch(
        commonSlice.actions.handleToast({
          isOpen: true,
          content: AI_STRINGS.ERROR_DISCONNECT_FAILED,
          error: true,
        }),
      );
    }
  };

  return (
    <BlockStack gap="400">
      {!isAskCardDismissed && (
        <AskAiAssistantCard onDismiss={() => dispatch(dismissAskCard(shop))} />
      )}
      <ProviderHubCard
        claudeConnection={claudeConnection}
        claudeError={!!error}
        isClaudeLoading={isFetching}
        onClaudeRetry={refetch}
        onClaudeConnect={handleConnectClaude}
        onClaudeDisconnect={() => setShowDisconnect(true)}
        onOpenHowTo={setHowToProvider}
      />
      <HowToConnectModal
        provider={howToProvider}
        onClose={() => setHowToProvider(null)}
      />
      <DisconnectModal
        open={showDisconnect}
        loading={deleteState.isLoading}
        onCancel={() => setShowDisconnect(false)}
        onConfirm={handleConfirmDisconnect}
      />
    </BlockStack>
  );
};

export default AiAssistants;
